import { useState, useEffect, useRef, lazy, Suspense } from "react";
import type { FC } from "react";
import { FaChevronDown } from "react-icons/fa";
import { useTranslation } from "react-i18next";

const PlanetScene = lazy(() => import("./PlanetScene"));

// ---------------------- ContactUs Component ----------------------
interface Option {
  value: string;
  label: string;
}

export const ContactUs: FC = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState<boolean>(false);
  const [selected, setSelected] = useState<string>(t("contact_subject"));
  const [scale, setScale] = useState<number>(getScale());
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const options: Option[] = [
    { value: "support", label: t("contact_customer_support") },
    { value: "project", label: t("contact_project_inquiry") },
    { value: "feedback", label: t("contact_feedback") },
  ];

  function getScale(): number {
    if (typeof window === "undefined") return 2;
    if (window.innerWidth < 640) return 1.2;
    if (window.innerWidth < 768) return 1.5;
    if (window.innerWidth < 1024) return 1.8;
    return 2.5;
  }

  useEffect(() => {
    const handleResize = () => setScale(getScale());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // The planet is ~2.9 MB of model and textures at the very bottom of the page.
  // Mount it only once the section is near the viewport.
  const planetRef = useRef<HTMLDivElement>(null);
  const [showPlanet, setShowPlanet] = useState(false);

  useEffect(() => {
    const el = planetRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setShowPlanet(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShowPlanet(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px 0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("sending");

    try {
      const res = await fetch("https://formspree.io/f/xlgppjqj", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: selected,
          message: formData.message,
        }),
      });

      if (res.ok) {
        setStatus("success");
        setFormData({ name: "", email: "", message: "" });
        setSelected(t("contact_subject"));
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="w-full min-h-screen flex flex-col bg-[#002F37] lg:flex-row items-center justify-center relative overflow-hidden">
      <div className="absolute inset-0 bg-[#002F37]/80 backdrop-blur-sm"></div>

      <div className="relative z-10 flex flex-col lg:flex-row items-center justify-center gap-6 sm:gap-8 lg:gap-20 w-full h-full px-4 sm:px-6 md:px-10 lg:px-20 py-8 sm:py-10 lg:py-16">

        {/* Contact Form */}
        <div className="w-full lg:w-1/2 bg-[#002F37]/90 rounded-2xl sm:rounded-3xl px-5 sm:px-6 md:px-8 lg:px-10 py-6 sm:py-8 lg:py-10 shadow-2xl border border-[#7EDE56]/40 backdrop-blur-md transform transition hover:-translate-y-1 hover:shadow-2xl duration-500 max-w-2xl lg:max-w-none">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#7EDE56] mb-4 sm:mb-6">
            {t("contact_heading")}
          </h2>

          {/* Success Message */}
          {status === "success" && (
            <div className="mb-5 px-4 py-3 rounded-lg bg-[#7EDE56]/20 border border-[#7EDE56] text-[#7EDE56] text-sm sm:text-base font-medium">
              {t("contact_success")}
            </div>
          )}

          {/* Error Message */}
          {status === "error" && (
            <div className="mb-5 px-4 py-3 rounded-lg bg-red-500/20 border border-red-400 text-red-400 text-sm sm:text-base font-medium">
              {t("contact_error")}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5 lg:space-y-6">
            <input
              type="text"
              placeholder={t("contact_name")}
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg border border-[#167208] bg-[#002F37] text-[#F4FFEC] placeholder-[#F4FFEC]/70 focus:outline-none focus:ring-2 focus:ring-[#7EDE56]"
            />
            <input
              type="email"
              placeholder={t("contact_email")}
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg border border-[#167208] bg-[#002F37] text-[#F4FFEC] placeholder-[#F4FFEC]/70 focus:outline-none focus:ring-2 focus:ring-[#7EDE56]"
            />

            {/* Subject Dropdown */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setOpen(!open)}
                className="w-full flex justify-between items-center px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg border border-[#167208] bg-[#002F37] text-[#F4FFEC] focus:outline-none focus:ring-2 focus:ring-[#7EDE56]"
              >
                <span className="truncate">{selected}</span>
                <FaChevronDown
                  className={`ml-2 transition-transform flex-shrink-0 ${open ? "rotate-180" : ""}`}
                  size={14}
                />
              </button>

              {open && (
                <ul className="absolute mt-2 w-full bg-[#ffffff] border border-[#7EDE56] rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto">
                  {options.map((opt) => (
                    <li
                      key={opt.value}
                      onClick={() => {
                        setSelected(opt.label);
                        setOpen(false);
                      }}
                      className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base cursor-pointer hover:bg-[#7EDE56]/40 transition"
                    >
                      {opt.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <textarea
              placeholder={t("contact_message")}
              rows={4}
              required
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg border border-[#167208] bg-[#002F37] text-[#F4FFEC] placeholder-[#F4FFEC]/70 focus:outline-none focus:ring-2 focus:ring-[#7EDE56] resize-none"
            />

            <button
              type="submit"
              disabled={status === "sending"}
              className="w-full cursor-pointer bg-[#7EDE56] text-[#002F3F] hover:bg-[#167208] hover:text-[#F4FFEC] font-semibold py-2.5 sm:py-3 text-sm sm:text-base rounded-lg shadow-md transition duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {status === "sending" ? t("contact_sending") : t("contact_send")}
            </button>
          </form>
        </div>

        {/* 3D Planet */}
        <div
          ref={planetRef}
          className="hidden sm:flex relative w-full lg:w-1/2 h-[300px] sm:h-[350px] md:h-[400px] lg:h-[500px] xl:h-[600px] items-center justify-center"
        >
          {showPlanet && (
            <Suspense fallback={null}>
              <PlanetScene scale={scale} />
            </Suspense>
          )}
        </div>

      </div>
    </div>
  );
};
